import React, { useState, useEffect } from 'react';
import { useSearchkitVariables } from '@searchkit/client';
import { usePrevious } from '@uncinc/uncinc-react-kitchen-sink';
import PropTypes from 'prop-types';

const SearchResultItemDefault = ({ item }) => (
  <div>
    <h3>{item.fields.title[0]}</h3>
  </div>
);

const SearchResultHeaderDefault = ({ data }) => (
  <h2>{data.results.summary.total} Results</h2>
);

const SearchResults = ({ data, renderItem, renderHeader, appendResults = false, scrollView = null }) => {
  const SearchResultItem = renderItem || SearchResultItemDefault;
  const SearchResultHeader = renderHeader || SearchResultHeaderDefault;
  const variables = useSearchkitVariables();

  const [items, setItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(-1);
  const [previousQuery, setPreviousQuery] = useState(variables.query);
  const [previousActiveFilters, setPreviousActiveFilters] = useState(variables.filters);
  const [previousSortBy, setPreviousSortBy] = useState(variables.sortBy);
  const [searchResultItems, setSearchResultItems] = useState(<></>);
  const [currentData, setCurrentData] = useState('');

  const previousPage = usePrevious(currentPage);
  const previousItems = usePrevious(items);

  useEffect(() => {
    if (typeof data.results === 'undefined') {
      return;
    }

    setCurrentPage(data.results.hits.page.pageNumber);
    setPreviousQuery(variables.query);
    setPreviousSortBy(variables.sortBy);
    setPreviousActiveFilters(variables.filters);
    setCurrentData(JSON.stringify(data));

    if (!appendResults) {
      // Set items directly if not appendResults
      return setItems([...data.results.hits.items]);
    }

    const currentPageNumber = data.results.hits.page.pageNumber;
    const currentQuery = variables.query;
    const currentFiltersLength = variables.filters.length;
    const newData = JSON.stringify(data);
    const currentSortBy = variables.sortBy;

    const queryChanged = previousQuery !== currentQuery;
    const filtersChanged = previousActiveFilters.length !== currentFiltersLength;
    const sortByChanged = currentSortBy !== previousSortBy;
    const pageChanged = currentPageNumber !== previousPage;
    const dataChanged = currentData !== newData;

    // Determine if we have to reset the results, or should append them to the results
    if (dataChanged && (queryChanged || filtersChanged || pageChanged || sortByChanged)) {
      if (typeof previousItems === 'undefined' || queryChanged || filtersChanged || sortByChanged) {
        // Overwrite data when Query, Filters or SortBy has changed
        setItems([...data.results.hits.items]);
      } else {
        // Append data if the page has changed and we have previousItems
        setItems([...previousItems, ...data.results.hits.items]);
      }
    }
  }, [data, appendResults, variables]);

  // Set the search results when items have changed
  useEffect(() => {
    setSearchResultItems(items.map((item) => <SearchResultItem key={item.id} item={item} />));
  }, [items]);

  // Scroll to top on every page.
  useEffect(() => {
    if (!currentData) {
      return;
    }
    const currentPageNumber = currentData?.results?.hits?.page?.pageNumber;
    if (appendResults === false && scrollView && scrollView.current && currentPageNumber !== 0) {
      scrollView.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentData, appendResults, scrollView]);

  return (
    <div className="SearchResults">
      {data && <SearchResultHeader data={data} />}
      {searchResultItems}
    </div>
  );
};

SearchResults.defaultProps = {
  renderItem: SearchResultItemDefault,
  renderHeader: SearchResultHeaderDefault,
  appendResults: false,
  scrollView: null,
};

SearchResults.propTypes = {
  // its turned off due to complex struxture of the object
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object.isRequired,
  renderItem: PropTypes.elementType,
  renderHeader: PropTypes.elementType,
  appendResults: PropTypes.bool,
  scrollView: PropTypes.element,
};

export default SearchResults;
